import {useEffect, useState} from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import styled from 'styled-components';
import {useChangePassword} from '@/api/mypage';
import {getGqlErrorMessage} from '@/api/request';
import {Button} from '@/components/Button';
import {CheckIconComponent} from '@/components/Icons';
import {TextField} from '@/components/TextField';
import {Typography, typographyStyle} from '@/components/Typography';
import {TextFieldLabel} from '@/components/UserSession/StyledComponents';
import {PWD_STRENGTH_LIST, PWD_STRENGTH_STATE, PWD_STRENGTH_STATE_COLOR} from '@/constants';
import {PwdStrengthConditionType} from '@/enums';
import {ColumnFlexBox, Flexbox, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {getPasswordStrength, getPwdStrengthLevel, hasInvalidCharacterInPwd} from '@/utils';
import toast from '../HotToast';
import {Modal} from '../Modal';

interface Inputs {
  currentPassword: string;
  password: string;
  passwordCheck: string;
}

interface Values {
  currentPassword?: string;
  password?: string;
  passwordCheck?: string;
}

interface IProps {
  onComplete?(): void;
  onClose?(): void;
}

export const UpdatePassword = ({onClose, onComplete}: IProps) => {
  const [values, setValues] = useState<Values>({});

  const {
    register,
    handleSubmit,
    watch,
    setFocus,
    setError,
    formState: {errors, isValid},
  } = useForm<Inputs>({mode: 'all'});

  const {mutate} = useChangePassword();

  const strengthCondition = getPasswordStrength(values?.password);
  const strengthLevel = hasInvalidCharacterInPwd(values?.password)
    ? 0
    : getPwdStrengthLevel(strengthCondition);
  const strengthState =
    !!values?.password?.length && PWD_STRENGTH_STATE[strengthLevel];

  const passwordStrengthValidate = (value: string) => {
    if (hasInvalidCharacterInPwd(value)) {
      return '유효하지 않은 문자가 포함되어 있습니다.';
    }

    if (!getPwdStrengthLevel(getPasswordStrength(value))) {
      return PWD_STRENGTH_STATE[0];
    }
  };

  const passwordCheckValidate = (value: string, formValues: Inputs) => {
    if (formValues.password !== value) {
      return '비밀번호가 일치하지 않습니다.';
    }
  };

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    mutate(
      {
        newPassword: data.password,
        oldPassword: data.currentPassword,
      },
      {
        onSuccess: (data) => {
          if (data.result) {
            onComplete?.();
          }
        },
        onError: (error) => {
          let message = getGqlErrorMessage(error, false);

          if (message?.length) {
            if (message === '패스워드가 틀립니다.') {
              message = '비밀번호가 일치하지 않습니다.';
            }

            setError('currentPassword', {
              type: 'server-error',
              message,
            });
          } else {
            toast.error(
              '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
            );
          }
        },
      }
    );
  };

  useEffect(() => {
    setFocus('currentPassword');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const subscription = watch((value) => {
      setValues((prev) => ({...prev, ...value}));
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  const pwdError = errors.password?.message;
  const passCharCondition =
    strengthCondition.filter((i) => PWD_STRENGTH_LIST.some((j) => j.type === i))
      ?.length > 1;

  return (
    <Modal width={328} onClose={onClose}>
      <PaddingBox spacing={24} as="form" onSubmit={handleSubmit(onSubmit)}>
        <ColumnFlexBox gap={16}>
          <Label>비밀번호</Label>
          <div>
            <TextFieldLabel error={!!errors.currentPassword?.message?.length}>
              현재 비밀번호
            </TextFieldLabel>
            <TextField
              {...register('currentPassword', {
                required: '비밀번호를 입력해 주세요.',
              })}
              fullWidth
              hasMaskButton
              variant="xlarge"
              placeholder="비밀번호를 입력해 주세요."
              type="password"
              autoComplete="new-password"
              maxLength={100}
              value={values.currentPassword}
              error={errors.currentPassword?.message}
            />
          </div>
          <div>
            <TextFieldLabel error={!!pwdError?.length}>
              새 비밀번호
            </TextFieldLabel>
            <FieldWrapper>
              <TextField
                {...register('password', {
                  validate: passwordStrengthValidate,
                  required: '비밀번호를 입력해 주세요.',
                })}
                fullWidth
                hasMaskButton
                variant="xlarge"
                placeholder="8자 이상, 문자/숫자/기호 사용"
                type="password"
                autoComplete="new-password"
                maxLength={100}
                value={values.password}
                error={pwdError === PWD_STRENGTH_STATE[0] ? true : pwdError}
              />
              <StrengthLevel
                style={{color: PWD_STRENGTH_STATE_COLOR[strengthLevel]}}
              >
                {strengthState}
              </StrengthLevel>
            </FieldWrapper>
            <PaddingBox top={8}>
              <ConditionCheckItem
                passed={strengthCondition.includes(
                  PwdStrengthConditionType.minLength
                )}
                label="8자 이상"
              />
              <PaddingBox top={4}>
                <ConditionCheckItem
                  passed={passCharCondition}
                  label="영소문자/영대문자/숫자/기호 중 최소 2가지 포함"
                />
              </PaddingBox>
            </PaddingBox>
          </div>
          <div>
            <TextFieldLabel error={!!errors.passwordCheck?.message?.length}>
              새 비밀번호 확인
            </TextFieldLabel>
            <TextField
              {...register('passwordCheck', {
                required: '입력하신 비밀번호를 다시 한번 입력해 주세요.',
                validate: passwordCheckValidate,
              })}
              fullWidth
              hasMaskButton
              variant="xlarge"
              placeholder="비밀번호를 입력해 주세요."
              type="password"
              autoComplete="new-password"
              maxLength={100}
              value={values.passwordCheck}
              error={errors.passwordCheck?.message}
            />
          </div>
        </ColumnFlexBox>
        <PaddingBox top={24}>
          <Button
            type="submit"
            disabled={!isValid}
            variant="fill"
            color="secondary"
            size="mediumLarge"
            fullWidth
          >
            저장
          </Button>
        </PaddingBox>
      </PaddingBox>
    </Modal>
  );
};

const ConditionCheckItem = ({
  passed,
  label,
}: {
  passed?: boolean;
  label: string;
}) => {
  return (
    <Flexbox>
      <CheckIconComponent
        style={{paddingRight: 2}}
        color={passed ? palette.green400 : palette.gray300}
      />
      <Typography variant="medium12" color={palette.gray600}>
        {label}
      </Typography>
    </Flexbox>
  );
};

const Label = styled.label`
  ${{...typographyStyle.bold16}}
`;

const FieldWrapper = styled.div`
  position: relative;
`;

const StrengthLevel = styled.div`
  ${{...typographyStyle.medium14}}
  position: absolute;
  right: 40px;
  top: 15px;
  display: flex;
  align-items: center;
  pointer-events: none;
`;
